import { createFileRoute } from "@tanstack/react-router";
import { GraduationCap, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import {
  modules,
  canAccessModule,
  TIER_LABEL,
  type AcademyTier,
} from "@/lib/academy";
import { useLessonProgress } from "@/hooks/useLessonProgress";
import { Skeleton } from "@/components/ui/skeleton";
import { ModuleCard } from "@/components/dashboard/academy/ModuleCard";

export const Route = createFileRoute("/_authenticated/dashboard/academy/")({
  component: AcademyHome,
});

function AcademyHome() {
  const { profile } = useAuth();
  const tier = (profile?.tier ?? "free") as AcademyTier;

  const { completed, loading } = useLessonProgress();

  const allLessons = modules.flatMap((m) => m.lessons);
  const totalUnlocked = allLessons.length;
  const doneUnlocked = allLessons.filter((l) => completed.has(l.slug)).length;
  const overallPct =
    totalUnlocked === 0 ? 0 : Math.round((doneUnlocked / totalUnlocked) * 100);

  return (
    <div className="min-h-full bg-slate-deep">
      <div className="container mx-auto max-w-7xl px-4 py-10">
        {/* Header */}
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold-muted px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
              <GraduationCap className="h-3 w-3" /> XAU Pro Academy
            </div>
            <h1 className="mt-3 font-display text-3xl font-bold md:text-4xl">
              Your Path to <span className="text-gradient-gold">Gold Mastery</span>
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              A structured curriculum, not a blog. Move through six modules of
              SMC, liquidity, top-down analysis, prop-firm execution, and systematic trading at your own pace.
            </p>
          </div>

          <div className="rounded-xl bg-white/[0.015] px-4 py-3 backdrop-blur-sm md:min-w-[260px]">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Current plan
            </div>
            <div className="mt-1 font-display text-base font-bold text-gold">
              {TIER_LABEL[tier]}
            </div>
          </div>
        </div>

        {/* Learning progress */}
        <div className="mt-8 rounded-2xl bg-white/[0.015] p-6 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-gold" />
                <span className="text-sm font-semibold">Learning Progress</span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {loading ? (
                  <Skeleton className="h-3 w-44" />
                ) : (
                  <>{doneUnlocked} of {totalUnlocked} unlocked lessons completed</>
                )}
              </div>
            </div>
            <div className="font-display text-2xl font-bold text-gradient-gold">
              {loading ? <Skeleton className="h-7 w-14" /> : `${overallPct}%`}
            </div>
          </div>
          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-border/60">
            <div
              className="h-full rounded-full bg-gradient-gold shadow-gold transition-all"
              style={{ width: loading ? "0%" : `${overallPct}%` }}
            />
          </div>
        </div>

        {/* Modules */}
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {modules.map((m) => (
            <ModuleCard
              key={m.id}
              module={m}
              unlocked={canAccessModule(tier, m.requiredTier)}
              completed={completed}
              userTier={tier}
            />
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-muted-foreground">
          New chapters released monthly. Members never pay extra.
        </p>
      </div>
    </div>
  );
}
